<template>
  <v-dialog v-model="dialogValue" persistent max-width="290">
    <v-card>
      <v-card-title class="text-primary text-h5">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text class="text-primary">
        {{ dialogConfirm }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          class="rounded-2"
          color="primary"
          variant="flat"
          @click="dialogValue = false"
        >
          <div
            class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
          >
            Cancel
          </div>
        </v-btn>
        <v-btn
          class="rounded-2"
          color="warning"
          variant="flat"
          :loading="loadingDelete"
          :disabled="loadingDelete"
          @click="handleDelete"
        >
          <div
            class="text-subtitle-2 text-white text-truncate font-weight-bold px-3"
          >
            Delete
          </div>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import API from '@/Core.Service.Domain/Sites/SiteSystems/API/index.ts';
import type { ISite, ISiteSystem } from '@/Core.Service.Domain/Sites/SiteSystems/types/index.ts';

const props = defineProps<{
  dialog: boolean;
  siteSystemInfo: ISiteSystem;
}>();

const emit = defineEmits<{
  'update:dialog': [value: boolean];
}>();

const route = useRoute();
const store = useStore();

const loadingDelete = ref(false);

const defaultSite = computed<ISite & { siteId?: number }>(
  () => store.getters['session/defaultSite']
);

const dialogValue = computed({
  get: () => props.dialog,
  set: (value: boolean) => emit('update:dialog', value),
});

const dialogTitle = computed<string>(
  () => `Delete this ${(route.meta?.toolTip as string) || 'Site System'}?`
);

const dialogConfirm = computed<string>(
  () =>
    `Are you sure you want to delete ${props.siteSystemInfo.siteSystemName}? This cannot be undone.`
);

async function handleDelete(): Promise<void> {
  const { siteSystemId } = props.siteSystemInfo;
  try {
    loadingDelete.value = true;
    await API.deleteSiteSystem(siteSystemId);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    const siteId: number = defaultSite.value?.siteId ?? defaultSite.value?.id ?? 0;
    await store.dispatch('SitesStore/SiteSystemsStore/setSiteSystems', siteId);
    dialogValue.value = false;
    loadingDelete.value = false;
  }
}
</script>
